import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import InvestorCatalogDashboard from '../components/InvestorCatalogDashboard';
import StartupDashboardContent from '../components/startup-dashboard/StartupDashboardContent';
import RateRegistrationPopup from '../components/profile/RateRegistrationPopup';
import AccessErrorScreen from '../components/AccessErrorScreen';
import { useStartupDashboardActivity } from '../utils/useStartupDashboardActivity';
import { reportAccessFailure } from '../utils/reportAccessFailure';

const USER_LOAD_TIMEOUT_MS = 20000;

const Spinner: React.FC<{ message?: string }> = ({ message }) => (
  <div className="min-h-screen bg-dark-950 flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4" />
      {message && <p className="text-white">{message}</p>}
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const { user, firebaseUser, userLoading, userLoadError, refreshUser } = useAuth();
  const {
    startupProfile,
    investorProfile,
    loading: profileLoading,
    profileLoadAttempted,
    error: profileError,
    reloadProfile,
  } = useProfile();
  const {
    interestedInvestors,
    activityTimeline,
    loading: startupDashboardLoading,
    error: startupDashboardError,
    refetch: refetchStartupDashboard,
  } = useStartupDashboardActivity();
  const navigate = useNavigate();
  const location = useLocation();
  const [userLoadTimedOut, setUserLoadTimedOut] = useState(false);
  const [profileUpdatedToastMessage, setProfileUpdatedToastMessage] = useState<string | null>(null);
  const [showRatePopup, setShowRatePopup] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!firebaseUser || user || userLoadError) {
      setUserLoadTimedOut(false);
      return;
    }
    if (!userLoading) return;

    const timer = window.setTimeout(() => {
      setUserLoadTimedOut(true);
      reportAccessFailure({
        kind: 'dashboard_load_timeout',
        route: '/dashboard',
        message: 'Account data did not load within 20 seconds',
      });
    }, USER_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [firebaseUser, user, userLoading, userLoadError]);

  useEffect(() => {
    if (user?.userType !== 'startup') return;
    const state = location.state as { toastMessage?: string; showRatePopup?: boolean } | null;
    if (state?.toastMessage) {
      setProfileUpdatedToastMessage(state.toastMessage);
      window.history.replaceState({}, document.title);
      const timer = window.setTimeout(() => setProfileUpdatedToastMessage(null), 10000);
      return () => window.clearTimeout(timer);
    }
    // Legacy: rating now opens on profile complete; clear stale nav state if present.
    if (state?.showRatePopup) {
      if (!user.rating) setShowRatePopup(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user]);

  useEffect(() => {
    if (!startupDashboardError || user?.userType !== 'startup') return;
    reportAccessFailure({
      kind: 'dashboard_data_failed',
      route: '/dashboard',
      message: startupDashboardError,
    });
  }, [startupDashboardError, user?.userType]);

  // Redirect to profile wizard when loaded and no profile exists (not a fetch error)
  useLayoutEffect(() => {
    if (!user || !profileLoadAttempted || profileLoading || profileError) return;
    if (user.userType === 'startup' && !startupProfile) {
      navigate('/profile/startup', { replace: true });
    }
    if (user.userType === 'investor' && !investorProfile) {
      navigate('/express-signup', { replace: true });
    }
  }, [
    user,
    profileLoading,
    profileLoadAttempted,
    profileError,
    startupProfile,
    investorProfile,
    navigate,
  ]);

  if (!firebaseUser) return <Navigate to="/login" />;

  if (userLoadError || userLoadTimedOut) {
    return (
      <AccessErrorScreen
        title="Unable to load your account"
        message={
          userLoadError ||
          'Your account is taking longer than expected to load. Please try again or contact support.'
        }
        onRetry={() => {
          setUserLoadTimedOut(false);
          void refreshUser();
        }}
      />
    );
  }

  if (userLoading || !user) {
    return <Spinner message="Loading your account..." />;
  }

  if (profileLoadAttempted && profileError) {
    return (
      <AccessErrorScreen
        title="Unable to load your profile"
        message={profileError}
        onRetry={() => void reloadProfile()}
      />
    );
  }

  if (user.userType === 'investor') {
    if (profileLoadAttempted && !investorProfile && !profileError) {
      return <Spinner message="Taking you to complete your profile..." />;
    }
    return <InvestorCatalogDashboard />;
  }

  if (user.userType === 'startup') {
    if (profileLoading || !profileLoadAttempted) return <Spinner message="Loading..." />;
    if (!startupProfile && !profileError) {
      return <Spinner message="Taking you to complete your profile..." />;
    }
    if (startupDashboardError && !startupDashboardLoading) {
      return (
        <AccessErrorScreen
          title="Unable to load dashboard"
          message={startupDashboardError}
          onRetry={() => void refetchStartupDashboard()}
        />
      );
    }
    if (startupDashboardLoading) return <Spinner message="Loading dashboard..." />;
    return (
      <>
        <StartupDashboardContent
          interestedInvestors={interestedInvestors}
          activityTimeline={activityTimeline}
          onResponseSubmitted={() => refetchStartupDashboard()}
        />
        {profileUpdatedToastMessage ? (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100000] px-5 py-3 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl max-w-[90vw] flex items-center gap-3"
            role="status"
            aria-live="polite"
          >
            <svg
              className="w-5 h-5 text-emerald-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-white text-sm font-medium">{profileUpdatedToastMessage}</p>
          </div>
        ) : null}
        <RateRegistrationPopup isOpen={showRatePopup} onClose={() => setShowRatePopup(false)} />
      </>
    );
  }

  return <Spinner message="Loading..." />;
};

export default DashboardPage;
